import React, { useState, useEffect } from 'react';
import { Box, Chip, Paper, Typography, CircularProgress } from '@mui/material';
import { FolderOpen, X } from 'lucide-react';
import useAuthStore from '../../Zustand/Store/useAuthStore';
import { MAIN_URL } from '../../Configurations/Urls';

/**
 * SavedViewsBar Component
 * Displays saved views as chips that can be clicked to load the view
 */
function SavedViewsBar({ 
  datagridKey, 
  onLoadView,
  onViewSaved 
}) {
  const { userData } = useAuthStore();
  const [savedViews, setSavedViews] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch saved views when datagridKey or user changes
  useEffect(() => {
    const fetchSavedViews = async () => {
      if (!datagridKey || !userData?.application_user_id || !userData?.organization?.organization_id) {
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organization-datagrid-user-views/user/${userData.application_user_id}?datagrid_key=${datagridKey}&organization_id=${userData.organization.organization_id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          setSavedViews(result.views || []);
        }
      } catch (error) {
        console.error('Error fetching saved views:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSavedViews();
  }, [datagridKey, userData?.application_user_id, userData?.organization?.organization_id, onViewSaved]);

  // Handle loading a view
  const handleLoadView = (view) => {
    if (onLoadView) {
      onLoadView(view);
    }
  };

  // Handle deleting a view
  const handleDeleteView = async (event, viewId) => {
    event.stopPropagation(); // Prevent triggering the chip click
    
    if (!window.confirm('Are you sure you want to delete this saved view?')) {
      return;
    }

    try {
      const response = await fetch(
        `${MAIN_URL}/api/organization-datagrid-user-views/${viewId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        // Remove the view from the local state
        setSavedViews(prevViews => 
          prevViews.filter(view => view.organization_datagrid_user_view_id !== viewId)
        );
      } else {
        const error = await response.json();
        alert(`Failed to delete view: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting saved view:', error);
      alert('Failed to delete view. Please try again.');
    }
  };

  // Don't render if no saved views or loading
  if (loading) {
    return (
      <Paper
        elevation={1}
        sx={{
          mb: 2,
          p: 1.5,
          backgroundColor: '#fafafa',
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading views...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (savedViews.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={1}
      sx={{
        mb: 2,
        p: 1.5,
        backgroundColor: '#f0f7ff',
        borderRadius: 1,
        border: '1px solid #b3d9ff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            mr: 1,
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <FolderOpen size={16} />
          Saved Views:
        </Typography>
        {savedViews.map((view) => (
          <Chip
            key={view.organization_datagrid_user_view_id}
            label={view.view_name}
            onClick={() => handleLoadView(view)}
            onDelete={(event) => handleDeleteView(event, view.organization_datagrid_user_view_id)}
            deleteIcon={<X size={14} />}
            icon={<FolderOpen size={14} />}
            sx={{
              backgroundColor: '#e3f2fd',
              color: 'primary.main',
              border: '1px solid #90caf9',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#bbdefb',
                borderColor: '#64b5f6',
              },
              '& .MuiChip-label': {
                px: 1.5,
                py: 0.5,
              },
              '& .MuiChip-deleteIcon': {
                color: 'primary.main',
                '&:hover': {
                  color: 'error.main',
                },
              },
            }}
          />
        ))}
      </Box>
    </Paper>
  );
}

export default SavedViewsBar;

